/**
 * InformeDiaDoc — el documento de liquidación de UN día para UNA compañía.
 *
 * ES UN PAPEL QUE SALE DE YOOL. No es la pantalla del día: ésa sirve para operar, ésta
 * para que la aseguradora cruce el abono contra sus pólizas. Por eso no lleva controles ni
 * estados de proceso — sólo lo que la compañía necesita para conciliar.
 *
 * DOS TEXTOS SE APARTAN DEL DISEÑO, y en los dos casos porque los datos lo desmienten:
 *
 *  1. La nota «base de cálculo» ya no dice que el CLP sea la UF convertida al valor del
 *     día. Verificado en dev: 3 de 12 cobros no se reproducen así, uno por 164 pesos.
 *  2. Un día sin movimiento no se explica por el fin de semana. Se cobra los siete días
 *     del calendario; los hábiles sólo mandan en la fecha del giro.
 *
 * @see src/components/payments/yoolpay/informe/documento.js
 * @see docs/superpowers/specs/2026-08-14-informes-liquidacion-design.md §3
 */
import React from 'react';

import { formatearRut } from '../../../../lib/rut';
import HojaA4, { Caja, Dato, Nota, RejillaDatos } from './HojaA4';
import TablaBloque from './TablaBloque';
import {
  TINTA,
  fmtBanco,
  fmtClpInforme,
  fmtDiaSemanaLargo,
  fmtFechaDoc,
  fmtFechaLargaDoc,
  fmtTipoCuenta,
  fmtUf,
  fmtValorUf,
  hoyDoc,
  textoMotivo,
} from './documento';

const MONO = { fontFamily: 'var(--font-mono, ui-monospace, monospace)' };

/** El RUT del documento: canónico en el dato, con puntos al mostrarlo. */
const rutVisible = (rut) => (rut ? `RUT ${formatearRut(rut)}` : null);

/**
 * El pie del documento, armado con lo que el emisor tenga configurado.
 *
 * Cada pedazo ausente simplemente no aparece: las `YOOL_EMISOR_*` son opcionales y un
 * campo vacío en un pie institucional se lee como un dato que se perdió.
 */
function pieEmisor(emisor = {}) {
  return [
    emisor.razon_social,
    emisor.rut ? `RUT ${formatearRut(emisor.rut)}` : null,
    'Recaudación por cuenta de terceros',
    'YoolPay',
  ]
    .filter(Boolean)
    .join(' · ');
}

/** El origen de los fondos. Sin banco configurado, el campo entero se omite. */
function origenFondos(emisor = {}) {
  if (!emisor.banco) return null;
  return [emisor.banco, `${fmtTipoCuenta(emisor.tipo_cuenta)} ${emisor.cuenta ?? ''}`.trim()]
    .filter(Boolean)
    .join(' · ');
}

/** El bloque de la cuenta de destino, o el motivo por el que no hay. */
function CuentaDestino({ destino }) {
  const girable = destino?.girable === true;
  return (
    <Caja style={{ margin: '16px 0 0' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '0 0 8px' }}>
        <img
          src="/ds/assets/yoolpay-isotipo.svg"
          alt=""
          style={{ height: 13, width: 'auto', display: 'block' }}
        />
        <p
          style={{
            margin: 0,
            fontSize: 8,
            fontWeight: 700,
            letterSpacing: '.12em',
            textTransform: 'uppercase',
            color: TINTA.rotulo,
          }}
        >
          {girable ? 'Cuenta de destino del giro' : 'Sin cuenta de destino registrada'}
        </p>
      </div>

      {girable ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px 16px' }}>
          <Dato
            rotulo="Titular"
            valor={destino.destino.nombre || '—'}
            bajada={rutVisible(destino.destino.rut)}
          />
          <Dato
            rotulo="Banco y cuenta"
            valor={fmtBanco(destino.destino.banco_sbif)}
            bajada={
              <>
                {fmtTipoCuenta(destino.destino.tipo_cuenta)}{' '}
                <span style={MONO}>{destino.destino.cuenta}</span>
              </>
            }
          />
          <Dato rotulo="Aviso de transferencia" valor={destino.destino.email || '—'} />
        </div>
      ) : (
        <Nota style={{ margin: 0, color: TINTA.suave }}>{destino?.motivo ?? '—'}</Nota>
      )}

      <Nota style={{ margin: '9px 0 0', fontSize: 9, color: TINTA.tenue }}>
        Los datos bancarios son los registrados en el mandato de recaudación vigente. Un
        cambio de cuenta se toma solo por instrucción escrita de la compañía y aplica desde
        la jornada siguiente a su confirmación.
      </Nota>
    </Caja>
  );
}

/** Un rótulo de cifra grande del pie del total. */
function CifraGrande({ rotulo, children }) {
  return (
    <div>
      <p
        style={{
          margin: '0 0 1px',
          fontSize: 8,
          fontWeight: 700,
          letterSpacing: '.09em',
          textTransform: 'uppercase',
          color: TINTA.rotulo,
        }}
      >
        {rotulo}
      </p>
      <p style={{ margin: 0, fontSize: 21, fontWeight: 700, letterSpacing: '-.02em' }}>
        {children}
      </p>
    </div>
  );
}

export default function InformeDiaDoc({ informe }) {
  if (!informe) return null;

  const filas = informe.total?.filas ?? [];
  const hayFilas = filas.length > 0;
  const origen = origenFondos(informe.emisor);

  return (
    <HojaA4
      rotulo={`Informe de liquidación · ${informe.compania_nombre ?? 'sin compañía'}`}
      eyebrow="Informe 1 — Liquidación del día"
      titulo={`Recaudación del ${fmtFechaLargaDoc(informe.fecha)}`}
      pie={pieEmisor(informe.emisor)}
    >
      <RejillaDatos>
        <Dato
          rotulo="Compañía"
          valor={informe.compania_nombre ?? 'Sin clasificar'}
          bajada={rutVisible(informe.compania_rut)}
        />
        <Dato
          rotulo="Fecha de recaudación"
          valor={fmtFechaDoc(informe.fecha)}
          bajada={fmtDiaSemanaLargo(informe.fecha)}
        />
        {/* «DE REFERENCIA» Y NO «DEL DÍA», a propósito: el CLP de cada fila es lo
            efectivamente cobrado y no se reconstruye con este valor. */}
        <Dato
          rotulo="Valor UF de referencia"
          valor={`$ ${fmtValorUf(informe.valor_uf_referencia_clp)}`}
          bajada={`fuente: ${informe.valor_uf_fuente ?? '—'}`}
        />
        <Dato
          rotulo="Informe"
          valor={informe.numero}
          bajada={`emitido ${fmtFechaDoc(hoyDoc())}`}
        />
      </RejillaDatos>

      {hayFilas ? (
        <>
          <TablaBloque titulo="Venta nueva" bajada="primera cuota" bloque={informe.venta_nueva} />
          <TablaBloque
            titulo="Cobranza"
            bajada="cuotas 2 en adelante, cartera vigente"
            bloque={informe.cobranza}
          />
          <TablaBloque
            titulo="Sin clasificar"
            bajada="no se pudo determinar el número de cuota"
            bloque={informe.sin_clasificar}
          />

          <div
            style={{
              breakInside: 'avoid',
              margin: '24px 0 0',
              borderTop: `2px solid ${TINTA.texto}`,
              padding: '12px 0 0',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-end',
                justifyContent: 'space-between',
                gap: 24,
              }}
            >
              <div>
                <p
                  style={{
                    margin: '0 0 3px',
                    fontSize: 8,
                    fontWeight: 700,
                    letterSpacing: '.12em',
                    textTransform: 'uppercase',
                    color: TINTA.marca,
                  }}
                >
                  Total a girar — {filas.length}{' '}
                  {filas.length === 1 ? 'orden de pago' : 'órdenes de pago'}
                </p>
                <p
                  style={{
                    margin: 0,
                    fontSize: 10,
                    color: TINTA.tenue,
                    maxWidth: '34em',
                    lineHeight: 1.5,
                  }}
                >
                  Venta nueva {fmtUf(informe.venta_nueva.uf)} UF + cobranza{' '}
                  {fmtUf(informe.cobranza.uf)} UF. Prima bruta al 100%, sin descuentos.
                </p>
              </div>
              <div
                style={{
                  display: 'flex',
                  gap: 28,
                  textAlign: 'right',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                <CifraGrande rotulo="Total UF">{fmtUf(informe.total.uf)}</CifraGrande>
                <CifraGrande rotulo="Total CLP">
                  $ {fmtClpInforme(informe.total.clp)}
                </CifraGrande>
              </div>
            </div>

            <RejillaDatos borde={false}>
              <Dato
                rotulo="Fecha del giro"
                valor={fmtFechaDoc(informe.giro.fecha)}
                bajada={informe.giro.fecha_es_estimada ? 'estimada' : null}
              />
              <Dato
                rotulo="N.º de transferencia"
                valor={
                  informe.giro.referencia
                    ? <span style={MONO}>{informe.giro.referencia}</span>
                    : 'pendiente'
                }
              />
              <Dato
                rotulo="Origen de los fondos"
                valor={origen}
                bajada={
                  informe.emisor?.razon_social
                    ? `${informe.emisor.razon_social} · recaudación`
                    : null
                }
              />
              <Dato
                rotulo="Glosa"
                valor={<span style={{ ...MONO, fontSize: 10 }}>{informe.giro.glosa}</span>}
              />
            </RejillaDatos>
          </div>

          <CuentaDestino destino={informe.destino} />

          <Caja rotulo="Base de cálculo" style={{ margin: '20px 0 0' }}>
            <Nota>
              La conciliación se realiza sobre el monto en UF. El importe en pesos es el
              efectivamente cobrado y se informa como referencia de caja.
            </Nota>
            <Nota>
              Cada fila muestra la UF pactada y el peso cobrado tal como quedaron
              registrados al momento del cargo. El valor UF del encabezado es la referencia
              publicada para esa fecha: no se usa para convertir las filas ni las
              reconstruye.
            </Nota>
            <Nota style={{ margin: 0 }}>
              Se gira el 100% de la prima bruta recaudada. Las comisiones de recaudación e
              intermediación y el costo del procesador de pagos no se descuentan del giro:
              se facturan por separado.
            </Nota>
          </Caja>
        </>
      ) : (
        <Caja rotulo="Sin movimiento" style={{ margin: '22px 0 0' }}>
          <p
            style={{
              margin: '0 0 6px',
              fontSize: 12.5,
              fontWeight: 700,
              letterSpacing: '.02em',
            }}
          >
            {textoMotivo(informe.movimiento)}
          </p>
          <Nota style={{ margin: 0 }}>
            No hay cobros que conciliar en esta jornada. Se cobra cualquier día del
            calendario, incluidos sábados y domingos: los días hábiles sólo determinan la
            fecha del giro.
          </Nota>
        </Caja>
      )}

      {informe.truncado ? (
        <Nota style={{ margin: '12px 0 0', color: TINTA.suave }}>
          Este informe alcanzó el tope de filas por consulta y puede estar incompleto.
        </Nota>
      ) : null}
    </HojaA4>
  );
}
